import { useCallback } from 'react'
import { posthog } from '@/providers/PostHogProvider'

// Event names as constants for consistency
export const AnalyticsEvents = {
  // Search events
  SEARCH_PERFORMED: 'search_performed',
  SEARCH_RESULT_CLICKED: 'search_result_clicked',
  SEARCH_FILTER_CHANGED: 'search_filter_changed',
  SEARCH_MODE_CHANGED: 'search_mode_changed',

  // File events
  FILE_UPLOADED: 'file_uploaded',
  FILE_SELECTED: 'file_selected',
  FILE_DELETED: 'file_deleted',
  INPUT_METHOD_CHANGED: 'input_method_changed',
  URL_FETCHED: 'url_fetched',
  TEXT_PASTED: 'text_pasted',

  // Dictionary events
  DICTIONARY_LOOKUP: 'dictionary_lookup',
  DICTIONARY_SEARCH: 'dictionary_search',
  SYNONYM_CLICKED: 'synonym_clicked',
  RELATED_WORD_CLICKED: 'related_word_clicked',

  // Editor events
  DOCUMENT_CREATED: 'document_created',
  DOCUMENT_SAVED: 'document_saved',
  SPELLCHECK_FIX_APPLIED: 'spellcheck_fix_applied',
  AUTOCOMPLETE_ACCEPTED: 'autocomplete_accepted',
  AUTOCOMPLETE_DISMISSED: 'autocomplete_dismissed',
  EDITOR_TOOL_USED: 'editor_tool_used',

  // Navigation events
  PAGE_CHANGED: 'page_changed',
  TAB_CHANGED: 'tab_changed',
  SIDEBAR_TOGGLED: 'sidebar_toggled',

  // Settings events
  LANGUAGE_CHANGED: 'language_changed',
  THEME_CHANGED: 'theme_changed',
  SETTING_UPDATED: 'setting_updated',

  // Error events
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
} as const

type EventName = typeof AnalyticsEvents[keyof typeof AnalyticsEvents]

// Main analytics hook
export function useAnalytics() {
  // Generic track function
  const track = useCallback((event: EventName, properties?: Record<string, any>) => {
    if (posthog.__loaded) {
      posthog.capture(event, {
        timestamp: new Date().toISOString(),
        ...properties,
      })
    }
  }, [])

  // Search tracking
  const trackSearch = useCallback((query: string, options: {
    fileId?: number
    matchMode?: string
    positionMode?: string
    resultCount?: number
  }) => {
    track(AnalyticsEvents.SEARCH_PERFORMED, {
      query,
      query_length: query.length,
      ...options,
    })
  }, [track])

  const trackSearchResultClick = useCallback((result: {
    word: string
    category?: string
    position: number
  }) => {
    track(AnalyticsEvents.SEARCH_RESULT_CLICKED, result)
  }, [track])

  const trackSearchFilterChange = useCallback((filterType: string, value: string) => {
    track(AnalyticsEvents.SEARCH_FILTER_CHANGED, { filter_type: filterType, value })
  }, [track])

  // File tracking
  const trackFileUpload = useCallback((file: {
    name: string
    size: number
    type: string
  }) => {
    track(AnalyticsEvents.FILE_UPLOADED, {
      file_name: file.name,
      file_size: file.size,
      file_type: file.type,
    })
  }, [track])

  const trackFileSelect = useCallback((fileId: number, fileName: string) => {
    track(AnalyticsEvents.FILE_SELECTED, { file_id: fileId, file_name: fileName })
  }, [track])

  const trackInputMethodChange = useCallback((method: 'files' | 'paste' | 'url') => {
    track(AnalyticsEvents.INPUT_METHOD_CHANGED, { method })
  }, [track])

  const trackUrlFetch = useCallback((url: string, success: boolean) => {
    track(AnalyticsEvents.URL_FETCHED, {
      url_domain: new URL(url).hostname,
      success,
    })
  }, [track])

  // Dictionary tracking
  const trackDictionaryLookup = useCallback((word: string, found: boolean, resultCount: number) => {
    track(AnalyticsEvents.DICTIONARY_LOOKUP, {
      word,
      word_length: word.length,
      found,
      result_count: resultCount,
    })
  }, [track])

  const trackDictionarySearch = useCallback((query: string, resultCount: number) => {
    track(AnalyticsEvents.DICTIONARY_SEARCH, {
      query,
      query_length: query.length,
      result_count: resultCount,
    })
  }, [track])

  const trackSynonymClick = useCallback((originalWord: string, synonym: string) => {
    track(AnalyticsEvents.SYNONYM_CLICKED, { original_word: originalWord, synonym })
  }, [track])

  const trackRelatedWordClick = useCallback((originalWord: string, relatedWord: string) => {
    track(AnalyticsEvents.RELATED_WORD_CLICKED, { original_word: originalWord, related_word: relatedWord })
  }, [track])

  // Editor tracking
  const trackDocumentCreate = useCallback(() => {
    track(AnalyticsEvents.DOCUMENT_CREATED, {})
  }, [track])

  const trackDocumentSave = useCallback((wordCount: number, charCount: number) => {
    track(AnalyticsEvents.DOCUMENT_SAVED, { word_count: wordCount, char_count: charCount })
  }, [track])

  const trackSpellcheckFix = useCallback((originalWord: string, replacement: string) => {
    track(AnalyticsEvents.SPELLCHECK_FIX_APPLIED, { original_word: originalWord, replacement })
  }, [track])

  const trackAutocompleteAccept = useCallback((prefix: string, suggestion: string) => {
    track(AnalyticsEvents.AUTOCOMPLETE_ACCEPTED, { prefix, suggestion })
  }, [track])

  const trackAutocompleteDismiss = useCallback((prefix: string) => {
    track(AnalyticsEvents.AUTOCOMPLETE_DISMISSED, { prefix })
  }, [track])

  const trackEditorTool = useCallback((tool: string) => {
    track(AnalyticsEvents.EDITOR_TOOL_USED, { tool })
  }, [track])

  // Navigation tracking
  const trackPageChange = useCallback((page: string, previousPage?: string) => {
    track(AnalyticsEvents.PAGE_CHANGED, { page, previous_page: previousPage })
  }, [track])

  const trackTabChange = useCallback((tab: string, previousTab?: string) => {
    track(AnalyticsEvents.TAB_CHANGED, { tab, previous_tab: previousTab })
  }, [track])

  const trackSidebarToggle = useCallback((isOpen: boolean) => {
    track(AnalyticsEvents.SIDEBAR_TOGGLED, { is_open: isOpen })
  }, [track])

  // Settings tracking
  const trackLanguageChange = useCallback((language: string, previousLanguage?: string) => {
    track(AnalyticsEvents.LANGUAGE_CHANGED, { language, previous_language: previousLanguage })
  }, [track])

  const trackThemeChange = useCallback((theme: string) => {
    track(AnalyticsEvents.THEME_CHANGED, { theme })
  }, [track])

  // Error tracking
  const trackError = useCallback((error: string, context?: Record<string, any>) => {
    track(AnalyticsEvents.ERROR_OCCURRED, { error, ...context })
  }, [track])

  const trackApiError = useCallback((endpoint: string, statusCode: number, error: string) => {
    track(AnalyticsEvents.API_ERROR, { endpoint, status_code: statusCode, error })
  }, [track])

  // Identify user (if you have user auth later)
  const identify = useCallback((userId: string, traits?: Record<string, any>) => {
    if (posthog.__loaded) {
      posthog.identify(userId, traits)
    }
  }, [])

  // Reset user (on logout)
  const reset = useCallback(() => {
    if (posthog.__loaded) {
      posthog.reset()
    }
  }, [])

  return {
    track,
    // Search
    trackSearch,
    trackSearchResultClick,
    trackSearchFilterChange,
    // Files
    trackFileUpload,
    trackFileSelect,
    trackInputMethodChange,
    trackUrlFetch,
    // Dictionary
    trackDictionaryLookup,
    trackDictionarySearch,
    trackSynonymClick,
    trackRelatedWordClick,
    // Editor
    trackDocumentCreate,
    trackDocumentSave,
    trackSpellcheckFix,
    trackAutocompleteAccept,
    trackAutocompleteDismiss,
    trackEditorTool,
    // Navigation
    trackPageChange,
    trackTabChange,
    trackSidebarToggle,
    // Settings
    trackLanguageChange,
    trackThemeChange,
    // Errors
    trackError,
    trackApiError,
    // User
    identify,
    reset,
  }
}

// Standalone tracking functions for use outside of React components
export const analytics = {
  track: (event: EventName, properties?: Record<string, any>) => {
    if (posthog.__loaded) {
      posthog.capture(event, {
        timestamp: new Date().toISOString(),
        ...properties,
      })
    }
  },

  identify: (userId: string, traits?: Record<string, any>) => {
    if (posthog.__loaded) {
      posthog.identify(userId, traits)
    }
  },

  reset: () => {
    if (posthog.__loaded) {
      posthog.reset()
    }
  },
}
